import React, { useState, useMemo, useEffect } from 'react'
import { Icon, Select, Input, Switch } from '@st-operations/uikit/src/v2'
import FormSelect from '_components/select/index.jsx'

import { useEventFeedsContext } from '../../context/index.jsx'
import {
  getTaxonomyRequiredErrors,
  isCustomTaxonomySelected,
  isCustomLabelEnabled,
  clearCustomLabel,
  buildFeedOutputRoot,
  getFeedOutputRootName,
  sanitizeFeedOutputSegment,
} from '../utils.js'
import Collapsible from '../collapsible/index.jsx'
import ManageComponent, { useIsSnapshot } from '../../manage-component/index.jsx'
import { getCustomTaxonomies } from '_services/data-explorer.js'

import './styles.scss'

const KECC_FIELDS = [{ label: 'Keywords', value: 'keywords' }]
const HEM_NPI_FORMATS = [
  { label: 'Parquet', value: 'parquet' },
  { label: 'CSV', value: 'csv' },
  { label: 'JSON', value: 'json' },
]

const CustomTaxonomyConfiguration = ({ }, ref) => {
  const { dispatch, ACTIONS, data, showTaxonomyErrors } = useEventFeedsContext()

  const [customTaxonomies, setCustomTaxonomies] = useState([])
  const isSnapshot = useIsSnapshot()

  // Errors recompute from `data`, so they clear live as the user fills fields in.
  const requiredErrors = showTaxonomyErrors ? getTaxonomyRequiredErrors(data) : {}

  const feedOutputRootName = getFeedOutputRootName(data?.customLabel?.feedOutputRoot)

  useEffect(() => {
    async function exec() {
      const { items } = await getCustomTaxonomies(undefined, { status: 'processed' })
      setCustomTaxonomies(items)
    }
    exec()
  }, [])

  const customTaxonomiesOptions = useMemo(
    () =>
      customTaxonomies
        .filter((f) => f.status === 'processed')
        .map((item) => ({ label: item.name, value: item._id })),
    [customTaxonomies]
  )

  const handleRemoveAll = () => {
    dispatch({
      type: ACTIONS.SET_DATA,
      // Custom Label only exists as part of a taxonomy, so removing the
      // taxonomy has to clear its attributes too.
      payload: {
        ...clearCustomLabel(data),
        customTaxonomy: [],
      },
    })
  }

  return (
    <Collapsible
      ref={ref}
      title="Custom Taxonomy"
      showOpenButton={false}
      onDeleteAllClick={handleRemoveAll}
      showDeleteAllButton={!isSnapshot && data.customTaxonomy?.length > 0}
    >
      <ManageComponent>
        <Select
          placeholder={
            <div className="custom-taxonomy-configuration__placeholder-search">
              <Icon icon="search" type="outline" color="#3D3D3D" />Quick search
            </div>
          }
          value={customTaxonomiesOptions.find((f) => f.value === data.customTaxonomy)}
          options={customTaxonomiesOptions}
          isSearchable
          onChange={({ value }) => {
            const updatedData = { ...data, customTaxonomy: value }
            // Ensure Topics Threshold has a sensible default when taxonomy is selected
            if (updatedData.topicsThreshold == null) {
              updatedData.topicsThreshold = 0.3
            }
            dispatch({ type: ACTIONS.SET_DATA, payload: updatedData })
          }}
          useReactSelect
          menuPlacement="top"
        />
        <If condition={isCustomTaxonomySelected(data)}>
          <Input
            label="Topics Threshold"
            name="topicsThreshold"
            value={data.topicsThreshold}
            onChange={(value) => {
              dispatch({
                type: ACTIONS.SET_DATA,
                payload: { ...data, topicsThreshold: Math.min(Math.max(Number(value), 0), 1) },
              })
            }}
            type="number"
            min={0}
            max={1}
            step={0.1}
            placeholder="0.3"
            description="Required"
            errorMessage={requiredErrors.topicsThreshold ? 'Topics Threshold is required' : ''}
          />
          <div className="mt-2 mb-4 flex items-center gap-2">
            <span className="text-[#3D3D3D]" style={{ fontSize: '14px', fontWeight: '600' }}>Custom Label</span>
            <Switch
              checked={isCustomLabelEnabled(data)}
              onChange={(e) => {
                const enabled = e.target.checked
                if (enabled) {
                  // Turning ON initializes defaults
                  dispatch({
                    type: ACTIONS.SET_DATA,
                    payload: {
                      ...data,
                      customLabel: {
                        keccField: 'keywords',
                        keccThreshold: 0.3,
                        hemNpiFormat: null,
                        feedOutputRoot: buildFeedOutputRoot(data?.client?.name || ''),
                      },
                    },
                  })
                } else {
                  // Turning OFF clears payload
                  dispatch({
                    type: ACTIONS.SET_DATA,
                    payload: clearCustomLabel(data),
                  })
                }
              }}
            />
          </div>
          <If condition={isCustomLabelEnabled(data)}>
            <FormSelect
              label="KECC Field"
              name="keccField"
              className="mb-5"
              options={KECC_FIELDS}
              value={KECC_FIELDS.find((f) => f.value === data?.customLabel?.keccField)}
              onChange={({ value }) => {
                dispatch({
                  type: ACTIONS.SET_DATA,
                  payload: { ...data, customLabel: { ...data.customLabel, keccField: value } },
                })
              }}
              isDisabled={true}
              description="Required"
            />
            <Input
              label="KECC Threshold"
              name="keccThreshold"
              value={data?.customLabel?.keccThreshold}
              onChange={(value) => {
                dispatch({
                  type: ACTIONS.SET_DATA,
                  payload: {
                    ...data,
                    customLabel: { ...data.customLabel, keccThreshold: Math.min(Math.max(Number(value), 0), 1) },
                  },
                })
              }}
              type="number"
              min={0}
              max={1}
              step={0.1}
              placeholder="0.3"
              description="Required"
              errorMessage={
                requiredErrors.customLabelKeccThreshold ? 'KECC Threshold is required' : ''
              }
            />
            <div>
              <Input
                label="Feed Output Root"
                name="feedOutputRoot"
                value={feedOutputRootName}
                onChange={(value) => {
                  dispatch({
                    type: ACTIONS.SET_DATA,
                    payload: {
                      ...data,
                      customLabel: {
                        ...data.customLabel,
                        feedOutputRoot: buildFeedOutputRoot(value),
                      },
                    },
                  })
                }}
                type="text"
                placeholder={sanitizeFeedOutputSegment(data?.client?.name || '')}
                description="Required"
                className="mb-1"
                errorMessage={
                  requiredErrors.customLabelFeedOutputRoot ? 'Feed Output Root is required' : ''
                }
              />
              {/* Always reserve space below the input: the uikit error message is
                  absolutely positioned (takes no layout space), so without this the
                  resolved-path preview or the error would overlap the next field. */}
              <div className="text-md text-[#a3a3a3] mb-4 min-h-[28px]">
                <If condition={Boolean(feedOutputRootName)}>{data.customLabel.feedOutputRoot}</If>
              </div>
            </div>
            <FormSelect
              label="HEM NPI Format"
              name="hemNpiFormat"
              description="Leave empty to not use HEM NPI"
              className="mb-5"
              options={HEM_NPI_FORMATS}
              value={HEM_NPI_FORMATS.find((f) => f.value === data?.customLabel?.hemNpiFormat)}
              onChange={(newValue) => {
                const value = newValue?.value || null
                dispatch({
                  type: ACTIONS.SET_DATA,
                  payload: { ...data, customLabel: { ...data.customLabel, hemNpiFormat: value } },
                })
              }}
              isClearable={true}
            />
          </If>
        </If>
      </ManageComponent>
    </Collapsible>
  )
}

export default React.forwardRef(CustomTaxonomyConfiguration)
